import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import qs from 'qs'

import { auth, refreshTokens } from '../actions/auth'
import { getActiveRegionId } from '../actions/region'
import { getAccessToken, isTokenExpired } from '../actions/token'
import { getUserType } from '../actions/user'
import { StorageIds } from '../constants/storage'
import { ID_API_URL, isDevMode, SOCCER_DATA_API_URL, SW_API_URL } from '../envs/envs'
import { TokenType } from '../types/authTypes'

interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  _retry: boolean
}

type FailedRequest = {
  resolve: (value: unknown) => void
  reject: (reason?: any) => void
}

export class ApiClient {
  private isTokenRefreshing = false
  private failedQueue: FailedRequest[] = []
  readonly api: AxiosInstance
  readonly authApi: AxiosInstance
  readonly soccerDataApi: AxiosInstance
  readonly platform: string

  constructor() {
    this.api = this.createInstance(SW_API_URL, true)
    this.authApi = this.createInstance(ID_API_URL)
    this.soccerDataApi = this.createInstance(SOCCER_DATA_API_URL, false)
    this.platform = localStorage.getItem(StorageIds.platform) || ''

    this.setApiToken()
  }

  private createInstance = (baseURL: string, useInterceptors = false) => {
    isDevMode && console.log(`[sdk https] createInstance: ${baseURL}`)

    if (!baseURL) {
      throw new Error('"baseURL" is not provided but required!')
    }

    const axiosConfig: AxiosRequestConfig = {
      baseURL,
      paramsSerializer: function (params) {
        return qs.stringify(params, { arrayFormat: 'repeat' })
      },
    }
    const axiosInstance = axios.create(axiosConfig)

    return useInterceptors ? this.setupInterceptors(axiosInstance) : axiosInstance
  }

  private onRequest = (config: AxiosRequestConfig): AxiosRequestConfig => {
    const token = getAccessToken()
    const userType = getUserType()
    const platform = localStorage.getItem(StorageIds.platform)
    const locale = navigator.language
    const regionId = getActiveRegionId()
    if (!token) {
      isDevMode && console.log('[sdk https] onRequest token ', token ? token?.slice(0, 20) + '...' : '❌')
    }

    config.params = { ...config.params, platform, locale, regionId, userType }
    if (config.headers) {
      config.headers['Authorization'] = token ? `Bearer ${token}` : ''
    }
    return config
  }
  private onRequestError = (error: AxiosError): Promise<AxiosError> => Promise.reject(error)
  private onResponse = (response: AxiosResponse): AxiosResponse => response
  private onResponseError = async (error: AxiosError): Promise<unknown> => {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const _self = this
    const originalRequest = error.config as ExtendedAxiosRequestConfig
    const refreshTokenIsExpired = isTokenExpired(TokenType.REFRESH)
    if (refreshTokenIsExpired) {
      await auth()
    } else {
      if (error.response?.status === 401 && !originalRequest._retry) {
        if (_self.isTokenRefreshing) {
          return new Promise((resolve, reject) => {
            _self.failedQueue.push({ resolve, reject })
          })
            .then((token) => {
              originalRequest.headers = {
                ...originalRequest.headers,
                Authorization: `Bearer ${token}`,
              }
              return api.request(originalRequest)
            })
            .catch((err) => {
              return Promise.reject(err)
            })
        }

        originalRequest._retry = true
        _self.isTokenRefreshing = true

        return new Promise((resolve, reject) => {
          refreshTokens()
            .then(() => {
              originalRequest.headers = originalRequest.headers ?? {}
              const token = getAccessToken()
              this.api.defaults.headers.common['Authorization'] = 'Bearer ' + token
              originalRequest.headers['Authorization'] = 'Bearer ' + token
              _self.processQueue(null, token)
              resolve(api.request(originalRequest))
            })
            .catch((err: Error) => {
              _self.processQueue(err, null)
              reject(err)
            })
            .finally(() => {
              _self.isTokenRefreshing = false
            })
        })
      }
    }
  }

  private processQueue = (error: Nullable<Error>, token: Nullable<string> = null) => {
    this.failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error)
      } else {
        prom.resolve(token)
      }
    })

    this.failedQueue = []
  }

  private setupInterceptors = (axiosInstance: AxiosInstance): AxiosInstance => {
    axiosInstance.interceptors.request.use(this.onRequest, this.onRequestError)
    axiosInstance.interceptors.response.use(this.onResponse, this.onResponseError)

    return axiosInstance
  }

  private setApiToken = () => {
    const token = getAccessToken()
    isDevMode && console.log('[sdk https] setApiToken', token?.slice(0, 20) + '...')
    if (token) {
      this.api.defaults.headers.common['Authorization'] = 'Bearer ' + token
    }
  }
}

export const { api, authApi, soccerDataApi } = new ApiClient()
